import {
  Alert,
  AlertIcon,
  AlertProps,
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Divider,
  Flex,
  FlexProps,
  Grid,
  GridItem,
  Text,
  TextProps,
  useDisclosure,
} from "@chakra-ui/react";
import { AmountSummary, TicketSummary } from "@picovico/ui";
import {
  catchErrorHandler,
  convertTimeTo12HourFormat,
  formatMsToMinutesSeconds,
  getLocalStorageData,
  storeLocalStorageData,
} from "@picovico/utils";
import { useEffect, useMemo, useState } from "react";
import {
  useLazyGetBookingDetailQuery,
  useLazyGetPaymentMethodsQuery,
  usePaymentInitiateMutation,
  useReserveSeatMutation,
} from "store/services/booking";

import { useAppToast } from "@/hooks/useAppToast";
import { useLoyaltyLimits } from "@/hooks/useLoyaltyLimits";
import { useOrderSession } from "@/hooks/useOrderSession";
import { useClearSeatSelectionMutation } from "@/store/services/audi";
import { useLazyGetBookingOrderDetailQuery } from "@/store/services/foodbooking";
import { isArray } from "@picovico/utils";
import { format } from "date-fns";
import ms from "ms";
import Error from "next/error";
import { useRouter } from "next/router";
import { FaArrowLeft } from "react-icons/fa";
import { getPageRoutes } from "routes/pageRoutes";
import { useAppSelector } from "store";
import { useGetLoyaltyEarningStatusQuery } from "store/services/user";
import { WebConfig } from "types/config";
import TimesUpModal from "../Modals/TimesUpModal";
import PaymentSelector from "../PaymentSelector";
import { useWebFeature } from "@/hooks/useWebFeature";
import { useWhatsAppCheckout } from "@/hooks/useWhatsApp";
import VoucherField, { VoucherFieldProps } from "./VoucherField";
import { VoucherValidateData } from "types/api";
import { WHATSAPP_CHECK_NUMBER } from "utils/whatsapp";
import BookingSkeleton from "./BookingSkeleton";
import { WhatsAppOptIn, WhatsAppOptInStyleProps } from "../WhatsApp";

function BookingConfirmation({
  displayAlert = false,
  alertProps = {},
  alertText = "Please select payment method before proceeding",
  gridProps = {},
  ticketSummaryGridItemProps = {},
  amountSummaryGridItemProps = {},
  paymentSelectorProps = {},
  amountSummaryBoxProps = {},
  ticketSummaryBoxProps = {},
  loyaltyDialogProps,
  dividerProps = {},
  reserveButtonProps,
  proceedButtonProps,
  topBoxProps,
  goBackButtonProps,
  timeRemainingTextProps,
  mobileLayout = "stacked",
  stickyPayBarProps,
  stickyPayBarLabelProps,
  stickyPayBarTotalProps,
  voucherFieldProps,
  whatsappOptInProps,
}: {
  displayAlert?: boolean;
  alertProps?: AlertProps;
  alertText?: string;
  gridProps?: BoxProps;
  ticketSummaryGridItemProps?: BoxProps;
  amountSummaryGridItemProps?: BoxProps;
  paymentSelectorProps?: {
    [key: string]: BoxProps;
  };
  amountSummaryBoxProps?: BoxProps;
  ticketSummaryBoxProps?: BoxProps;
  /**
   * Surface styling for the loyalty earning-limits dialog. Settable per tenant from the DB —
   * `sageweb_webcomponent.default_props` (all pages) or `sageweb_webpagecomponent.props` (one
   * page), which are shallow-merged and spread straight into this component. Falls back to the
   * theme's `colors.background.nav` when unset.
   */
  loyaltyDialogProps?: BoxProps;
  dividerProps?: BoxProps;
  reserveButtonProps?: ButtonProps;
  proceedButtonProps?: ButtonProps;
  topBoxProps?: FlexProps;
  goBackButtonProps?: ButtonProps;
  timeRemainingTextProps?: TextProps;
  /**
   * Mobile checkout layout:
   * - "stacked" (default): everything scrolls, Proceed button sits inline.
   * - "sticky": a fixed bottom bar (total + Proceed to Pay) on mobile; the
   *   inline action buttons are hidden below md.
   */
  mobileLayout?: "stacked" | "sticky";
  stickyPayBarProps?: FlexProps;
  stickyPayBarLabelProps?: TextProps;
  stickyPayBarTotalProps?: TextProps;
  voucherFieldProps?: Partial<VoucherFieldProps>;
  /** Styling for the "Send my ticket on WhatsApp" box (whatsapp-messaging 04 §3). */
  whatsappOptInProps?: WhatsAppOptInStyleProps;
}) {
  const router = useRouter();
  // The voucher box is gone while the company has discount_voucher off.
  const voucherEnabled = useWebFeature("discount_voucher");
  const config: WebConfig = useAppSelector((state) => state.config);
  // The themed surface colour the storefront uses elsewhere (see MembershipPoints/TicketHistory).
  // The loyalty card itself has no background of its own — it shows the page through — but the
  // limits dialog is portalled out, so it has to be told the colour explicitly or it renders on
  // Chakra's default white.
  const themeBackground = useAppSelector(
    // @ts-expect-error optional chaining through the tenant-supplied theme
    (state) => state.config?.site_theme?.colors?.background,
  );
  const { user } = useAppSelector((state) => state.user);

  const [paymentMethod, setPaymentMethod] = useState("");
  const [time, setTime] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [payViaLoyalty, setPayViaLoyalty] = useState(false);
  // Priced by the server, not here. Held in state only so the summary and the payload agree on
  // the same figure the customer was shown.
  const [voucher, setVoucher] = useState<VoucherValidateData | null>(null);
  // "Send my ticket on WhatsApp" (whatsapp.tickets and /whatsapp/me/ tickets; absent means off).
  const whatsapp = useWhatsAppCheckout("ticket");

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [getBookingDetail, { isLoading, data, error }] =
    useLazyGetBookingDetailQuery({
      pollingInterval: ms("5min"),
    });
  const [getBookingOrderDetail, { data: orderData }] =
    useLazyGetBookingOrderDetailQuery({});
  const [reserveSeats, { isLoading: isReserving }] = useReserveSeatMutation();
  const [clearSeats] = useClearSeatSelectionMutation();
  const [proceedPayment, { isLoading: paymentProcessing }] =
    usePaymentInitiateMutation();
  const route_booking_no = router.query?.booking_no;
  const bookingDetail = data?.data;
  const movieIdx = router.query.movieIdx as string;
  const showIdx = router.query.showIdx as string;
  const orderIdx = router.query.orderIdx as string;
  const { success, error: errorToast } = useAppToast();
  const { clearOrder } = useOrderSession();
  const [getPaymentMethods, { data: paymentMethodData }] =
    useLazyGetPaymentMethodsQuery();
  const company_idx = user.cinema_company.idx;
  useEffect(() => {
    if (company_idx && showIdx) {
      getPaymentMethods({
        company_idx: company_idx,
        show_idx: showIdx,
      });
    }
  }, [getPaymentMethods, showIdx, company_idx]);

  useEffect(() => {
    if (showIdx) {
      const local_booking_no = getLocalStorageData("booking_no");
      getBookingDetail({
        show_idx: showIdx,
        ...(route_booking_no || local_booking_no
          ? {
              booking_no: route_booking_no || local_booking_no,
            }
          : {}),
      });
    }
  }, [showIdx, getBookingDetail, route_booking_no]);

  useEffect(() => {
    if (orderIdx) {
      getBookingOrderDetail({ order_idx: orderIdx });
    }
  }, [orderIdx, getBookingOrderDetail]);

  /**
   * @param isCovered a voucher has left nothing to charge.
   *
   * Passed in by the caller rather than read from the enclosing scope: the flag is derived further
   * down this component, and reaching up for it here would be a use-before-define that only works
   * because clicks happen after render. Taking it as an argument says the same thing without
   * relying on that.
   */
  const handleProceedToPay = (isCovered = false) => {
    // A fully-covered sale has no tender to pick — the selector is hidden and `paymentMethod` is
    // deliberately empty — so guarding on it alone would swallow the click and leave the customer
    // pressing a button that silently does nothing. The server resolves the complimentary tender
    // itself in that case, and refuses a missing method on any sale that still has a balance.
    if (!paymentMethod && !isCovered) {
      return;
    }

    // The WhatsApp number is checked before anything is charged; its error shows under the field.
    const whatsappFields = whatsapp.prepare();
    if (!whatsappFields) {
      errorToast(WHATSAPP_CHECK_NUMBER);
      return;
    }
    const bookingNo =
      route_booking_no || getLocalStorageData("booking_no") || "";

    proceedPayment({
      booking_no: bookingNo,
      movie_show: showIdx,
      // Omitted entirely when there is nothing to charge; sending "" would fail validation.
      ...(paymentMethod ? { payment_method: paymentMethod } : {}),
      failure_url: window.location.origin + getPageRoutes("profile") || "",
      redirect_url:
        window.location.origin + getPageRoutes("payment_callback") || "",
      ...(orderIdx && { food_order: orderIdx }),
      ...(voucher ? { discount_code: voucher.code } : {}),
      // `whatsapp_to` (+ `whatsapp_offers: true` only when ticked); nothing when not asked for.
      ...whatsappFields,
    })
      .unwrap()
      .then((res) => {
        if (res.data) {
          if (res.data.booking_no)
            storeLocalStorageData("booking_no", res.data.booking_no);
          // The success page says where the ticket went; the gateway leaves the site in between.
          whatsapp.remember(whatsappFields, [
            res.data.pidx,
            res.data.booking_no || bookingNo,
          ]);

          // A voucher covered the whole amount, so there was nothing to charge and the server has
          // already closed the sale. Redirecting to a gateway for a zero payment would strand the
          // customer on a page that cannot complete — but they still bought a ticket, so they land
          // on the SAME callback page every other sale ends on, and download it there. The server
          // recognises the settled sale and answers without calling a payment provider, so the
          // success screen and the ticket download are the ordinary ones rather than a second,
          // parallel confirmation flow that would drift from this one.
          if (res.data.completed) {
            success("Booking confirmed — your voucher covered the full amount");
            router.push(
              `${getPageRoutes("payment_callback")}?pidx=${res.data.pidx}`,
            );
            clearOrder();

            return;
          }

          if (res.data.payment_url) window.location.href = res.data.payment_url;
        }
        clearOrder();
      })
      .catch((err) => {
        // 400 on `whatsapp_to`: shown under the number.
        if (whatsapp.showServerError(err)) {
          errorToast(WHATSAPP_CHECK_NUMBER);
          return;
        }
        catchErrorHandler(err, errorToast);
      });
  };

  const handleReserveSeats = () => {
    reserveSeats({
      movie_show: showIdx,
    })
      .unwrap()
      .then(() => {
        router.push(getPageRoutes("profile", {}, { tab: "bookings" }));
        success("Seats reserved successfully");
        clearOrder();
      })
      .catch((err) => {
        catchErrorHandler(err, errorToast);
      });
  };

  const amountBreakdown = useMemo(() => {
    if (!bookingDetail?.booking_info || !isArray(bookingDetail?.booking_info))
      return [];
    const tickets: {
      amount: number;
      quantity: number;
      title: string;
      showAmountUnitPrice: boolean;
      highlightItem?: boolean;
    }[] = [];

    bookingDetail.booking_info?.forEach((item) => {
      const ticketIndex = tickets.findIndex(
        (t) => t.title === item.ticket_type && t.amount === +item.price,
      );
      if (ticketIndex === -1) {
        tickets.push({
          title: item.ticket_type,
          amount: +item.price,
          quantity: 1,
          showAmountUnitPrice: true,
        });
      } else {
        tickets[ticketIndex].quantity += 1;
      }
    });

    return tickets;
  }, [bookingDetail]);

  const foodBreakdown = useMemo(() => {
    if (!orderData?.data) return [];

    const foodItems: {
      amount: number;
      quantity: number;
      title: string;
      showAmountUnitPrice: boolean;
      highlightItem?: boolean;
    }[] = [];

    orderData.data.menu_items?.forEach((item) => {
      foodItems.push({
        title: item.title,
        amount: item.amount / item.quantity,
        quantity: item.quantity,
        showAmountUnitPrice: true,
      });
    });

    orderData.data.combo_packages?.forEach((item) => {
      foodItems.push({
        title: item.title,
        amount: item.amount / item.quantity,
        quantity: item.quantity,
        showAmountUnitPrice: true,
      });
    });

    return foodItems;
  }, [orderData]);

  // Per-seat prices, needed for a SEATS voucher to be priced exactly (it frees the N cheapest).
  const seatPrices = useMemo(
    () =>
      (bookingDetail?.booking_info ?? []).map(
        (item) => +item.price + (bookingDetail?.three_d_charge || 0),
      ),
    [bookingDetail],
  );

  const ticketSubtotal = useMemo(() => {
    const { normalAmount, threeDAmount } = amountBreakdown.reduce(
      (acc, cur) => {
        acc.normalAmount += cur.amount * cur.quantity;
        acc.threeDAmount += (bookingDetail?.three_d_charge || 0) * cur.quantity;
        return acc;
      },
      { normalAmount: 0, threeDAmount: 0 },
    );

    return normalAmount + threeDAmount;
  }, [amountBreakdown, bookingDetail]);

  const foodSubtotal = useMemo(() => {
    return orderData?.data?.order_total_amount || 0;
  }, [orderData]);

  // The whole basket, not just the seats. A BOTH voucher discounts the tickets and the food
  // together; a scoped one discounts only its own half and leaves the other at full price. The
  // server decided which, from the voucher's `applies_to`, and returned the split — this only has
  // to show the total and net it off.
  const voucherDiscount = Math.min(
    voucher?.discount_amount ?? 0,
    ticketSubtotal + foodSubtotal,
  );

  const total = useMemo(() => {
    return ticketSubtotal + foodSubtotal - voucherDiscount;
  }, [ticketSubtotal, foodSubtotal, voucherDiscount]);

  // Nothing left to charge: the server settles this as complimentary without a gateway, so there
  // is no payment method to pick. Hiding the selector beats disabling it — a greyed-out row asks
  // a question ("why can't I?") that has no useful answer here.
  const isFullyCovered = voucherDiscount > 0 && total <= 0;

  // A barred member sees no loyalty at checkout at all — no balance, no earn projection, no
  // loyalty payment option. Nulling the config is what hides the whole AmountSummary panel.
  const isLoyaltyBarred = !!user?.member?.is_blacklisted;
  const checkoutLoyaltyConfig = isLoyaltyBarred ? null : config.loyalty_config;

  // Live standing against the earning caps, straight from the server that enforces them.
  const { data: earningStatus } = useGetLoyaltyEarningStatusQuery(
    {},
    {
      skip: isLoyaltyBarred || !config.loyalty_config?.enable || !user?.member,
    },
  );
  const { earnAllowance, loyaltyLimits } = useLoyaltyLimits(
    checkoutLoyaltyConfig,
    earningStatus?.data,
  );

  // Sticky mobile pay bar: floats above the bottom nav (if the tenant uses one)
  // so the total + Proceed action is always reachable without scrolling.
  const isSticky = mobileLayout === "sticky";
  const hasBottomNav =
    config.layout_config?.mobile_config?.nav_style === "bottom_nav";
  const stickyBarBottom = hasBottomNav
    ? "calc(3.9rem + env(safe-area-inset-bottom))"
    : "env(safe-area-inset-bottom)";
  const canReserve =
    bookingDetail?.payment_status?.toLowerCase() !== "reserved" &&
    bookingDetail?.allow_reserve &&
    !orderIdx;

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    if (isRunning && time > 0) {
      intervalId = setInterval(() => {
        setTime((prevTime) => {
          const newTime = prevTime - 1000;
          if (newTime <= 0) {
            setIsRunning(false);
            clearSeats({
              movie_show: router.query.showIdx,
            });
            clearOrder();
            onOpen();
            return 0;
          }
          return newTime;
        });
      }, 1000);
    } else if (bookingDetail?.expire_at && !isRunning) {
      const diff = bookingDetail.expire_at * 1000;
      if (diff < 300000 && diff > 0) {
        setTime(diff);
        setIsRunning(true);
      }
    } else {
      setTime(300000);
      setIsRunning(false);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [
    isRunning,
    time,
    bookingDetail,
    clearSeats,
    clearOrder,
    onOpen,
    router.query.showIdx,
  ]);

  if (error) {
    return <Error statusCode={404} />;
  }

  return isLoading ? (
    <BookingSkeleton />
  ) : bookingDetail ? (
    <>
      <Grid
        templateColumns={{
          base: "repeat(1, 1fr)",
          md: "repeat(12, 1fr)",
        }}
        gap="1rem"
        // Reserve room so the sticky mobile bar doesn't cover the last content.
        pb={isSticky ? { base: "5.5rem", md: 0 } : undefined}
        {...gridProps}
      >
        <GridItem colSpan={{ base: 1, md: 12 }}>
          <Flex
            justify="space-between"
            align="center"
            gap="0.5rem"
            color="gray.500"
            fontSize={{
              base: "sm",
              md: "xl",
            }}
            {...topBoxProps}
          >
            <Button
              leftIcon={<FaArrowLeft size={14} />}
              variant="ghost"
              color="gray.600"
              fontWeight="normal"
              size={{ base: "sm", md: "md" }}
              px={{ base: 2, md: 4 }}
              flexShrink={0}
              onClick={() => router.back()}
              {...goBackButtonProps}
            >
              Go Back
            </Button>
            <Box
              aria-live="polite"
              aria-label={`Elapsed time: ${formatMsToMinutesSeconds(time)}-booking-confirmation`}
              textAlign="right"
              flexShrink={0}
              {...timeRemainingTextProps}
            >
              {/* Compact label on mobile so the timer doesn't crowd the button */}
              <Box as="span" display={{ base: "none", sm: "inline" }}>
                Time Remaining to book:{" "}
              </Box>
              <Box as="span" display={{ base: "inline", sm: "none" }}>
                Time left:{" "}
              </Box>
              {formatMsToMinutesSeconds(time)}
            </Box>
          </Flex>
        </GridItem>
        <GridItem
          colSpan={{
            base: 1,
            md: 8,
          }}
          {...ticketSummaryGridItemProps}
        >
          <TicketSummary
            seats={
              bookingDetail?.booking_info?.map((bItem) => bItem.seat) || []
            }
            movieDetails={{
              title: bookingDetail?.show_info?.movie || "",
              cinemaHall: bookingDetail?.show_info?.cinema_hall || "",
              poster: bookingDetail?.show_info?.movie_poster || "",
              show_start_date_time: bookingDetail?.show_info
                ?.show_start_date_time
                ? `${format(
                    new Date(bookingDetail?.show_info?.show_start_date_time),
                    "MMMM d yyyy",
                  )} • ${convertTimeTo12HourFormat(
                    bookingDetail.show_info.show_start_date_time.slice(11, 16),
                  ).replace(
                    /(\d+):(\d+)/,
                    (_, h, m) => `${h.padStart(2, "0")}:${m.padStart(2, "0")}`,
                  )}`
                : "",
              auditorium: bookingDetail?.show_info?.auditorium || "",
            }}
            {...ticketSummaryBoxProps}
          >
            {displayAlert && (
              <Alert status="info" mt="1rem" {...alertProps}>
                <AlertIcon />
                {alertText}
              </Alert>
            )}
            <Divider my="1rem" {...dividerProps} />

            {foodSubtotal > 0 && checkoutLoyaltyConfig?.enable && (
              <Alert status="info" mb="1rem">
                <AlertIcon />
                Loyalty points cannot be used for orders that include food
                items.
              </Alert>
            )}

            {voucherEnabled && (
              <VoucherField
                ticketSubtotal={ticketSubtotal}
                foodSubtotal={foodSubtotal}
                movieShow={showIdx}
                seatPrices={seatPrices}
                applied={voucher}
                onApplied={(applied) => {
                  setVoucher(applied);
                  // A fully-covered sale has no tender to choose, and leaving a stale selection
                  // behind would be sent on the payload.
                  if (
                    applied &&
                    applied.discount_amount >= ticketSubtotal + foodSubtotal
                  ) {
                    setPaymentMethod("");
                    setPayViaLoyalty(false);
                  }
                }}
                isDisabled={paymentProcessing}
                {...voucherFieldProps}
              />
            )}

            <WhatsAppOptIn
              whatsapp={whatsapp}
              isDisabled={paymentProcessing}
              {...whatsappOptInProps}
              // Styled like the voucher field above unless the theme styles
              // this box itself (no site theme passes `whatsappOptInProps`).
              containerProps={{
                borderColor: dividerProps.borderColor,
                ...whatsappOptInProps?.containerProps,
              }}
              inputProps={{
                ...voucherFieldProps?.inputProps,
                ...whatsappOptInProps?.inputProps,
              }}
              errorMessageProps={{
                ...voucherFieldProps?.errorMessageProps,
                ...whatsappOptInProps?.errorMessageProps,
              }}
            />

            {isFullyCovered ? (
              <Alert status="success" mb="1rem">
                <AlertIcon />
                Your voucher covers the full amount — no payment needed.
              </Alert>
            ) : (
            <PaymentSelector
              value={paymentMethod}
              data={paymentMethodData?.data}
              member={user.member}
              loyaltyConfig={checkoutLoyaltyConfig}
              amount={total}
              disableLoyalty={foodSubtotal > 0}
              onSelected={(paymentMethodValue) => {
                setPaymentMethod(paymentMethodValue.idx);
                setPayViaLoyalty(
                  paymentMethodValue.payment_method === "LOYALTY_POINT",
                );
              }}
              {...paymentSelectorProps}
            />
            )}

            <Flex
              justifyContent="flex-end"
              gap={{
                base: 1,
                sm: 4,
              }}
              // In sticky mode the actions live in the fixed bottom bar on
              // mobile, so hide the inline buttons below md.
              display={isSticky ? { base: "none", md: "flex" } : "flex"}
            >
              {canReserve && (
                <Button
                  mt="2rem"
                  isLoading={isReserving || paymentProcessing}
                  onClick={handleReserveSeats}
                  colorScheme="surface"
                  variant="outline"
                  {...reserveButtonProps}
                >
                  Reserve Seats
                </Button>
              )}
              <Button
                mt="2rem"
                isLoading={paymentProcessing}
                isDisabled={
                  paymentProcessing || (!isFullyCovered && paymentMethod === "")
                }
                onClick={() => handleProceedToPay(isFullyCovered)}
                size={{
                  base: "sm",
                  sm: "md",
                }}
                {...proceedButtonProps}
              >
                {isFullyCovered ? "Confirm Booking" : "Proceed to Pay"}
              </Button>
            </Flex>
          </TicketSummary>
        </GridItem>
        <GridItem
          colSpan={{
            base: 1,
            md: 4,
          }}
          {...amountSummaryGridItemProps}
        >
          <AmountSummary
            total={total}
            voucherDiscount={voucherDiscount}
            voucherLabel={voucher?.code}
            // Tickets only, net of any ticket-side voucher saving — exactly the figure dolby puts
            // on the MOVIE transaction it awards points against. Food earns nothing, so including
            // it here quoted the customer points they would never be credited.
            loyaltyEarnableAmount={Math.max(
              ticketSubtotal - (voucher?.ticket_discount ?? 0),
              0,
            )}
            amountBreakdown={amountBreakdown}
            foodBreakdown={foodBreakdown}
            ticketSubtotal={ticketSubtotal}
            foodSubtotal={foodSubtotal}
            three_d_charge={bookingDetail.three_d_charge}
            loyaltyConfig={checkoutLoyaltyConfig}
            payViaLoyalty={payViaLoyalty}
            availableLoyaltyPoints={
              isLoyaltyBarred ? 0 : user?.member?.available_points || 0
            }
            loyaltyEarnAllowance={earnAllowance}
            loyaltyLimits={loyaltyLimits}
            loyaltyDialogProps={{
              bg: themeBackground?.nav ?? "surface.05",
              ...loyaltyDialogProps,
            }}
            {...amountSummaryBoxProps}
          />
        </GridItem>
      </Grid>
      {isSticky && (
        <Flex
          display={{ base: "flex", md: "none" }}
          position="fixed"
          left={0}
          right={0}
          bottom={stickyBarBottom}
          zIndex={20}
          align="center"
          justify="space-between"
          gap="0.75rem"
          px="1rem"
          py="0.75rem"
          bg="white"
          borderTop="1px solid"
          borderColor="blackAlpha.200"
          boxShadow="0 -4px 16px rgba(0,0,0,0.12)"
          sx={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
          {...stickyPayBarProps}
        >
          <Box minW={0}>
            <Text
              fontSize="xs"
              opacity={0.7}
              lineHeight="1.1"
              {...stickyPayBarLabelProps}
            >
              Total
            </Text>
            <Text
              fontSize="lg"
              fontWeight="bold"
              lineHeight="1.2"
              noOfLines={1}
              {...stickyPayBarTotalProps}
            >
              Rs. {total}
            </Text>
          </Box>
          <Flex gap="0.5rem" flexShrink={0}>
            {canReserve && (
              <Button
                size="sm"
                variant="outline"
                colorScheme="surface"
                isLoading={isReserving || paymentProcessing}
                onClick={handleReserveSeats}
                {...reserveButtonProps}
              >
                Reserve
              </Button>
            )}
            <Button
              isLoading={paymentProcessing}
              isDisabled={paymentProcessing || paymentMethod === ""}
              onClick={() => handleProceedToPay(isFullyCovered)}
              {...proceedButtonProps}
            >
              Proceed to Pay
            </Button>
          </Flex>
        </Flex>
      )}
      <TimesUpModal
        isOpen={isOpen}
        onClose={onClose}
        handleStartOver={() => {
          movieIdx && showIdx
            ? router.push(
                getPageRoutes("audi_detail", {
                  movieIdx,
                  showIdx,
                }),
              )
            : router.push("/");
          clearOrder();
        }}
      />
    </>
  ) : null;
}

export default BookingConfirmation;
